import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Body1,
    Button,
    Label,
    Spinner,
    Text,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { NotFoundError } from '../../libs/services/BaseService';
import { ActivationRequiredReason, SpirisService } from '../../libs/services/SpirisService';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { SpirisActivationSteps } from '../shared/SpirisActivationSteps';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: tokens.spacingVerticalS,
    },
    description: {
        color: tokens.colorNeutralForeground3,
    },
    error: {
        color: tokens.colorPaletteRedForeground1,
    },
});

type State = 'loading' | 'connected' | 'notConnected' | 'unavailable' | 'error';

/**
 * Settings section for the user's Spiris access: shows whether the assistant can reach Spiris, and offers
 * a new sign-in or a re-check when it can't. Renders nothing when the Spiris route is unavailable for this
 * user or host (the backend answers 404).
 */
export const SpirisConnectionSection: React.FC = () => {
    const classes = useClasses();
    const { t } = useTranslation();
    const auth = useAuth();
    const token = auth.user?.access_token;
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const [state, setState] = useState<State>('loading');
    const [reason, setReason] = useState<string | null>(null);
    const [activationUrl, setActivationUrl] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        if (!token) {
            return;
        }
        try {
            const connection = await new SpirisService(token).getConnectionAsync(hostContext);
            setReason(connection.reason);
            setActivationUrl(connection.activationUrl ?? null);
            setState(connection.connected ? 'connected' : 'notConnected');
        } catch (err) {
            setState(err instanceof NotFoundError ? 'unavailable' : 'error');
        }
    }, [token, hostContext]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    if (!token || state === 'unavailable') {
        return null;
    }

    const recheck = async () => {
        setBusy(true);
        try {
            const connection = await new SpirisService(token).recheckConnectionAsync(hostContext);
            setReason(connection.reason);
            setActivationUrl(connection.activationUrl ?? null);
            setState(connection.connected ? 'connected' : 'notConnected');
        } catch {
            setState('error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={classes.root} data-testid="spiris-connection-section">
            <Label>{t('spiris.settings.title')}</Label>
            <Body1 className={classes.description}>{t('spiris.settings.description')}</Body1>
            {state === 'loading' && <Spinner size="tiny" label={t('spiris.settings.checking')} />}
            {state === 'error' && <Text className={classes.error}>{t('spiris.settings.error')}</Text>}
            {state === 'connected' && (
                <Text data-testid="spiris-connection-status">{t('spiris.settings.connected')}</Text>
            )}
            {state === 'notConnected' && reason === ActivationRequiredReason && (
                <>
                    <Text data-testid="spiris-connection-status">{t('spiris.settings.notActivated')}</Text>
                    <Accordion collapsible>
                        <AccordionItem value="activation">
                            <AccordionHeader size="small">{t('spiris.settings.activationHowTo')}</AccordionHeader>
                            <AccordionPanel>
                                <SpirisActivationSteps activationUrl={activationUrl} />
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                    <Button
                        disabled={busy}
                        onClick={() => {
                            void recheck();
                        }}
                        data-testid="spiris-recheck"
                    >
                        {t('spiris.settings.recheck')}
                    </Button>
                </>
            )}
            {state === 'notConnected' && reason !== ActivationRequiredReason && (
                <>
                    <Text data-testid="spiris-connection-status">{t('spiris.settings.notConnected')}</Text>
                    <Button
                        appearance="primary"
                        onClick={() => {
                            void auth.signinRedirect();
                        }}
                        data-testid="spiris-connect"
                    >
                        {t('spiris.settings.signIn')}
                    </Button>
                </>
            )}
            {state === 'error' && (
                <Button
                    disabled={busy}
                    onClick={() => {
                        setState('loading');
                        void refresh();
                    }}
                    data-testid="spiris-retry"
                >
                    {t('spiris.settings.retry')}
                </Button>
            )}
        </div>
    );
};
